import React, { useState, useEffect, useRef } from "react";
import moment from "moment";

import { IDog, ISportProgress, IEarnedTitle, IDogRunSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import { Modal, useModal } from "../../../components/Modal/Modal";
import { ManualRunEntryModal } from "../ManualRunEntry";
import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";
import ContextMenu, { IContextMenuOption } from "../../../components/ContextMenu/ContextMenu";

import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz } from "ag-grid-community";

import "./FastCATCard.scss";

interface IProps {
  dog: IDog;
  sportData: ISportProgress;
  onRunUpdated?: () => void; // Callback to refresh data (optional for public view)
}

const FastCATCard: React.FC<IProps> = ({ dog, sportData, onRunUpdated }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRun, setEditingRun] = useState<IDogRunSummary | undefined>(undefined);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [runToDelete, setRunToDelete] = useState<string | null>(null);
  const [showRunsModal, setShowRunsModal] = useState(false);
  const [showBulkPointsModal, setShowBulkPointsModal] = useState(false);
  const [bulkPoints, setBulkPoints] = useState<string>('');
  const [isAddingBulkPoints, setIsAddingBulkPoints] = useState(false);
  const [localRuns, setLocalRuns] = useState<IDogRunSummary[]>(sportData.recent_runs || []);
  
  // Track if we need to refresh parent when modal closes
  const needsParentRefreshRef = useRef(false);
  

  // Sync localRuns with sportData when it changes
  useEffect(() => {
    setLocalRuns(sportData.recent_runs || []);
  }, [sportData.recent_runs]);
  
  // Initialize modalKey from localStorage if available (survives parent re-renders)
  const [modalKey, setModalKey] = useState<string>(() => {
    const stored = localStorage.getItem('fastcat_modal_key');
    return stored || Date.now().toString();
  });
  
  const deleteConfirmModal = useModal();

  const handleCloseRunsModal = () => {
    setShowRunsModal(false);
    
    // If we added/edited/deleted runs, refresh the parent to update title progress
    if (needsParentRefreshRef.current) {
      needsParentRefreshRef.current = false;
      onRunUpdated?.();
    }
  };

  const handleAddManualRun = () => {
    const newKey = Date.now().toString();
    localStorage.setItem('fastcat_modal_key', newKey); // Persist to survive parent refresh
    setEditingRun(undefined);
    setModalKey(newKey); // Generate new key for fresh modal
    setIsModalOpen(true);
  };

  const handleEditRun = (run: IDogRunSummary) => {
    const editKey = `edit-${run._id}`;
    localStorage.setItem('fastcat_modal_key', editKey); // Persist to survive parent refresh
    setEditingRun(run);
    setModalKey(editKey); // Use run ID as key for editing
    setIsModalOpen(true);
  };

  const handleDeleteRun = (runId: string) => {
    setRunToDelete(runId);
    deleteConfirmModal.toggle();
  };

  // Stable refs for AG Grid cell renderer callbacks (avoids stale closure issues)
  const handleEditRunRef = useRef(handleEditRun);
  const handleDeleteRunRef = useRef(handleDeleteRun);
  const deletingRunIdRef = useRef(deletingRunId);
  useEffect(() => { handleEditRunRef.current = handleEditRun; });
  useEffect(() => { handleDeleteRunRef.current = handleDeleteRun; });
  useEffect(() => { deletingRunIdRef.current = deletingRunId; }, [deletingRunId]);

  const getActionMenuOptions = (run: IDogRunSummary) => {
    if (!run) return null;
    const isManualEntry = run.data_source === Constants.run_data_source.manual_entry;
    if (run.verified) return null;
    if (deletingRunIdRef.current === run._id) return null;
    if (!isManualEntry) return null;
    return [
      { label: 'Edit', onClick: () => handleEditRunRef.current(run) },
      { label: 'Delete', onClick: () => handleDeleteRunRef.current(run._id) }
    ] as IContextMenuOption[];
  };

  const ActionsCellRenderer = (params: any) => {
    const run = params.data;
    if (!run) return null;
    if (run.verified) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#10b981', fontWeight: 600 }}>Verified</span>;
    if (deletingRunIdRef.current === run._id) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>Deleting...</span>;
    const options = params.getOptions(run);
    if (!options) return null;
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><ContextMenu options={options} disablePortal /></div>;
  };

  // AGGrid column definitions
  const columnDefs: ColDef[] = [
    {
      headerName: 'Date',
      field: 'trial_date',
      valueFormatter: (params) => moment(params.value).format('M/D/YYYY'),
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Time',
      field: 'time_seconds',
      valueFormatter: (params) => params.value ? `${params.value.toFixed(2)}s` : '-',
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Speed',
      field: 'mph',
      valueFormatter: (params) => params.value ? `${params.value.toFixed(2)} mph` : '-',
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Points',
      field: 'points_earned',
      valueFormatter: (params) => params.value ? `${params.value.toFixed(2)} pts` : '-',
      minWidth: 100,
      flex: 1,
      cellStyle: { color: '#F25A2D', fontWeight: 600 }
    },
    {
      headerName: 'Event',
      valueGetter: (params) => params.data.event?.name || 'Manual Entry',
      minWidth: 200,
      flex: 2
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: { getOptions: getActionMenuOptions },
      cellStyle: { overflow: 'visible', zIndex: 1 },
      minWidth: 150,
      flex: 1
    }
  ];

  const renderTitleBadge = (title: IEarnedTitle) => {
    return (
      <div 
        key={title.title_code} 
        className="titleBadge"
        style={{ backgroundColor: title.color_hex || '#4CAF50' }}
      >
        <div className="badgeInfo">
          <div className="badgeTitle">{title.title_code}</div>
          <div className="badgeDate">{moment(title.earned_date).format('M/D/YY')}</div>
        </div>
      </div>
    );
  };

  const renderProgressBar = () => {
    if (!sportData.next_title) {
      return (
        <div className="progressComplete">
          <div className="completeIcon">🌟</div>
          <div className="completeText">
            <strong>Maximum Title Achieved!</strong>
            <p>Congratulations on earning all available Fast CAT titles!</p>
          </div>
        </div>
      );
    }

    const percentage = sportData.next_title.percentage || 0;

    return (
      <div className="progressSection">
        <div className="progressHeader">
          <div className="progressLabel">
            Progress to <strong>{sportData.next_title.title_code}</strong>
          </div>
          <div className="progressStats">
            {sportData.next_title.points_needed} points needed
          </div>
        </div>
        <div className="progressBarContainer">
          <div 
            className="progressBarFill" 
            style={{ width: `${Math.min(percentage, 100)}%` }}
          >
            <span className="progressPercentage">{Math.min(percentage, 100).toFixed(1)}%</span>
          </div>
        </div>
        <div className="progressFooter">
          <span>{(sportData.total_points || 0).toFixed(2)} total points</span>
          <span>{sportData.total_runs} runs</span>
        </div>
      </div>
    );
  };

  const confirmDelete = async () => {
    if (!runToDelete) return;

    setDeletingRunId(runToDelete);
    deleteConfirmModal.toggle();
    
    try {
      await apiService.deleteManualRun(runToDelete);
      toast.show({
        title: "Run Deleted",
        content: "The run has been removed and title progress has been recalculated.",
        duration: 5000,
        type: "success"
      });
      
      // Refresh the local runs list
      const response = await apiService.getRibbonRoomData(dog._id);
      const fastCATSport = response.sports.find((s: ISportProgress) => s.show_type === 'Fast CAT');
      if (fastCATSport) {
        setLocalRuns(fastCATSport.recent_runs || []);
      }
      
      // Mark that parent needs refresh when modal closes
      needsParentRefreshRef.current = true;
    } catch (error: any) {
      console.error('Error deleting run:', error);
      toast.show({
        title: "Delete Failed",
        content: "Unable to delete the run. Please try again.",
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    } finally {
      setDeletingRunId(null);
      setRunToDelete(null);
    }
  };

  const handleModalSuccess = async () => {
    // Close the manual run entry modal
    setIsModalOpen(false);
    setEditingRun(undefined);
    
    // Fetch fresh run data to update the table without closing the modal
    try {
      const response = await apiService.getRibbonRoomData(dog._id);
      const fastCATSport = response.sports.find((s: ISportProgress) => s.show_type === 'Fast CAT');
      if (fastCATSport) {
        setLocalRuns(fastCATSport.recent_runs || []);
      }
    } catch (error) {
      console.error('Failed to refresh runs:', error);
    }
    
    // Mark that we need to refresh parent when the run history modal closes
    needsParentRefreshRef.current = true;
  };

  const handleAddBulkPoints = async () => {
    const points = parseFloat(bulkPoints);
    if (isNaN(points) || points <= 0) {
      toast.show({
        title: "Invalid Points",
        content: "Please enter a valid number of points greater than 0.",
        duration: 5000,
        type: "fail"
      });
      return;
    }

    setIsAddingBulkPoints(true);
    
    try {
      // Add bulk points using the dedicated endpoint
      await apiService.addBulkFastCATPoints({
        dog_id: dog._id,
        points: points,
        trial_date: new Date()
      });
      
      toast.show({
        title: "Points Added",
        content: `${points} points have been added to ${dog.call_name}'s Fast CAT progress.`,
        duration: 5000,
        type: "success"
      });
      
      setShowBulkPointsModal(false);
      setBulkPoints('');
      onRunUpdated?.(); // Refresh the data
    } catch (error: any) {
      console.error('Error adding bulk points:', error);
      toast.show({
        title: "Failed to Add Points",
        content: "Unable to add points. Please try again.",
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    } finally {
      setIsAddingBulkPoints(false);
    }
  };

  return (
    <div className="fastCATCard">
      <div className="cardHeader">
        <div className="headerLeft">
          <h3>AKC Fast CAT</h3>
          <p className="cardSubtitle">Coursing Ability Test</p>
        </div>
        <div className="headerRight">
          <div className="statsGrid">
            <div className="statItem">
              <div className="statLabel">Total Points</div>
              <div className="statValue">{(sportData.total_points || 0).toFixed(2)}</div>
            </div>
              <div className="statItem">
                <div className="statLabel">Fastest</div>
              <div className="statValue">{(sportData.stats?.fastest_mph || 0).toFixed(2)} <span className="unit">mph</span></div>
              </div>
              <div className="statItem">
                <div className="statLabel">Average</div>
              <div className="statValue">{(sportData.stats?.average_mph || 0).toFixed(2)} <span className="unit">mph</span></div>
              </div>
          </div>
        </div>
      </div>

      {/* Earned Titles */}
      {sportData.earned_titles && sportData.earned_titles.length > 0 && (
        <div className="earnedTitlesSection">
          <h4>Earned Titles ({sportData.earned_titles.length})</h4>
          <div className="titleBadges">
            {sportData.earned_titles.map(renderTitleBadge)}
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {renderProgressBar()}

      {/* Last Run Info */}
      {sportData.recent_runs && sportData.recent_runs.length > 0 && sportData.recent_runs[0] && (
        <div className="lastRunSection">
          <h4>Last Run - {moment(sportData.recent_runs[0].trial_date).format('MMMM Do, YYYY')}</h4>
          <div className="lastRunInfo">
            {sportData.recent_runs[0].time_seconds && (
              <span className="lastRunMetric">
                <span className="metricLabel">Time:</span> <span className="metricValue">{sportData.recent_runs[0].time_seconds.toFixed(2)}s</span>
              </span>
            )}
            {sportData.recent_runs[0].mph && (
              <span className="lastRunMetric">
                <span className="metricLabel">Speed:</span> <span className="metricValue">{sportData.recent_runs[0].mph.toFixed(2)} mph</span>
              </span>
            )}
            {sportData.recent_runs[0].points_earned && (
              <span className="lastRunMetric">
                <span className="metricLabel">Points:</span> <span className="metricValue">{sportData.recent_runs[0].points_earned.toFixed(1)}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* View Runs Button - Always show, even with 0 runs */}
      {onRunUpdated ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={() => setShowRunsModal(true)}
          >
            View & Edit Run History ({sportData.recent_runs?.length || 0} runs)
          </button>
        </div>
      ) : sportData.recent_runs && sportData.recent_runs.length > 0 ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={() => setShowRunsModal(true)}
          >
            View Run History ({sportData.recent_runs.length} runs)
          </button>
        </div>
      ) : null}

      {/* Runs Modal */}
      <Modal
        isShown={showRunsModal}
        hide={handleCloseRunsModal}
        className="runsHistoryModal"
        modalContent={
          <div className="runsModal">
            <h2>AKC Fast CAT - Run History</h2>

            <div className="runsModalContent">
              {/* Runs Table */}
              <div className="runsSection">
                <div className="runsSectionHeader">
                  <h4>All Runs</h4>
                </div>
                <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
                  <AgGridReact
                    rowData={localRuns}
                    columnDefs={columnDefs}
                    domLayout='normal'
                    suppressCellFocus={true}
                    theme={themeQuartz}
                    getRowId={(params) => params.data._id}
                  />
                </div>
              </div>

              {/* Add Manual Run Button - only show for owners */}
              {onRunUpdated && (
                <div className="modalActions">
                  <Button onClick={() => setShowBulkPointsModal(true)} variant="secondary">
                    + Add Past Points
                  </Button>
                  <Button onClick={handleAddManualRun}>
                    + Add Manual Run
                  </Button>
                </div>
              )}
            </div>
          </div>
        }
      />

      {/* Manual Run Entry Modal */}
      <ManualRunEntryModal
        key={modalKey} // Force remount with unique key each time
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingRun(undefined);
        }}
        dog={dog}
        sportType={Constants.show_type.fast_cat}
        sanctioningClub={Constants.sanctioning_club.AKC}
        onSuccess={handleModalSuccess}
        editRun={editingRun as any}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isShown={deleteConfirmModal.isShown}
        hide={deleteConfirmModal.toggle}
        className="deleteConfirmModal"
        modalContent={
          <div className="deleteConfirmModal">
            <h2>Delete Run</h2>
            <p>Are you sure you want to delete this run? This will recalculate your title progress.</p>
            <div className="modalActions">
              <Button onClick={deleteConfirmModal.toggle} variant="secondary">
                Cancel
              </Button>
              <Button onClick={confirmDelete} className="deleteButton">
                Delete
              </Button>
            </div>
          </div>
        }
      />

      {/* Bulk Points Modal */}
      <Modal
        isShown={showBulkPointsModal}
        hide={() => setShowBulkPointsModal(false)}
        className="bulkPointsModal"
        modalContent={
          <div className="bulkPointsModal">
            <h2>Add Past Points</h2>
            <p>Add points from past runs without entering full run details. Great for catching up on history!</p>
            <div className="formGroup">
              <label htmlFor="bulkPoints">Number of Points</label>
              <input
                id="bulkPoints"
                type="number"
                step="0.01"
                min="0"
                value={bulkPoints}
                onChange={(e) => setBulkPoints(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="e.g. 150"
                disabled={isAddingBulkPoints}
              />
              <small>Enter the total points you want to add to {dog.call_name}'s Fast CAT progress.</small>
            </div>
            <div className="modalActions">
              <Button 
                onClick={() => setShowBulkPointsModal(false)} 
                variant="secondary"
                disabled={isAddingBulkPoints}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddBulkPoints}
                disabled={isAddingBulkPoints || !bulkPoints}
              >
                {isAddingBulkPoints ? 'Adding...' : 'Add Points'}
              </Button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default FastCATCard;

